import { call, put, select } from 'typed-redux-saga'
import { AnyAction } from 'redux'

import { ApplicationState } from '@store/types'
import services from '@services'
import { BridgeService } from '@services/bridge'
import { PostDadosPessoaisRequest } from '@services/postDadosPessoaisService'
import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { StepRefin } from '@utils/enums'
import { clearAccountFields } from '@store/refinanciamento/dadosBancarios/actions'

import { PostDadosPessoaisPayload } from '../types'
import {
  postDadosPessoaisRequest,
  setBankAccountCorrectionMode,
  setHasDadosBancarios,
  setPostDadosPessoaisLoading,
  setRejectedBankAccount,
} from '../actions'
import { isInvalidModalityAccountError } from '../isInvalidModalityAccountError'
import { toBankData } from '../bankAccountCorrection'

type CustomerDataState = Pick<ApplicationState['refinanciamento'], 'endereco' | 'dadosBancarios'>

const buildPostDadosPessoaisRequest = ({
  endereco,
  dadosBancarios,
}: CustomerDataState): PostDadosPessoaisRequest => ({
  addressData: {
    zipCode: String(endereco.cep || ''),
    addressType: String(endereco.tipoLogradouro || ''),
    streetAddress: String(endereco.logradouro || ''),
    number: String(endereco.numero || ''),
    additionalInfo: String(endereco.complemento || ''),
    district: String(endereco.bairro || ''),
    state: String(endereco.uf || ''),
    city: String(endereco.cidade || ''),
  },
  bankData: toBankData(dadosBancarios),
})

function* startBankAccountCorrection(bankData: PostDadosPessoaisRequest['bankData']) {
  yield* put(setRejectedBankAccount(bankData))
  yield* put(setBankAccountCorrectionMode(true))
  yield* put(clearAccountFields())
  yield* put(setHasDadosBancarios(false))
}

export default function* postDadosPessoaisSaga(action: AnyAction) {
  const payload = action.payload as PostDadosPessoaisPayload
  let payloadRequest: PostDadosPessoaisRequest | undefined

  try {
    yield* put(setPostDadosPessoaisLoading(true))
    const { session, refinanciamento } = yield* select((state: ApplicationState) => state)

    payloadRequest = buildPostDadosPessoaisRequest(refinanciamento)

    yield* call(services.postDadosPessoaisService, payloadRequest, session.accessToken)

    yield* put(setLastServiceResponse({ code: 'OK', serviceName: 'postDadosPessoaisService' }))
    yield* put(setPostDadosPessoaisLoading(false))
    if (payload && typeof payload.callback === 'function') {
      payload.callback()
    }
  } catch (error) {
    yield* put(setPostDadosPessoaisLoading(false))
    yield* put(setLastServiceResponse({ code: 'ERROR', serviceName: 'postDadosPessoaisService' }))

    if (BridgeService.isBrowser() && payloadRequest && isInvalidModalityAccountError(error)) {
      yield* startBankAccountCorrection(payloadRequest.bankData)
      if (typeof payload?.invalidModalityAccountCallback === 'function') {
        payload.invalidModalityAccountCallback()
      }
      return
    }

    yield* put(
      showError({
        title: 'Erro ao salvar as informações',
        message: 'Ocorreu um erro ao salvar as informações',
        actionTitle: 'Tentar Novamente',
        code: StepRefin.ERRO_SALVAR_DADOS_PESSOAIS,
        actionCallback: () =>
          postDadosPessoaisRequest({
            callback: payload?.callback,
            errorCallback: payload?.errorCallback,
            invalidModalityAccountCallback: payload?.invalidModalityAccountCallback,
          }),
      }),
    )
    if (typeof payload?.errorCallback === 'function') {
      payload.errorCallback()
    }
  }
}
